import { useCallback } from 'react';
import { QueryKey, useQuery, useQueryClient } from 'react-query';
import { useApi } from 'hooks/useApi';
import { useUser } from 'state/auth';

export type SummaryDataKeys =
  | 'number_of_leads'
  | 'number_of_watch_leads'
  | 'number_of_star_leads'
  | 'number_of_projects'
  | 'number_of_watch_projects'
  | 'number_of_star_projects';

type SummaryResponse = {
  data: {
    [K in SummaryDataKeys]: number | string;
  };
  message: string;
};

export const useProjectData = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { token } = useUser();

  // This function can be used to invalidate multiple queries in batch
  const batchInvalidateQueries = useCallback(
    (queries: QueryKey[]) => {
      queries.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
    [queryClient],
  );

  // We'll use useCallback to memoize this function so it's not recreated on every render
  const invalidateGetGroupConfigLeadSummary = useCallback(() => {
    batchInvalidateQueries(['group-config-lead-summary']);
  }, [batchInvalidateQueries]);

  const getGroupConfigLeadsSummary = useCallback(
    (group_config_id: number) => {
      return useQuery(
        ['group-config-lead-summary', group_config_id, token],
        async (): Promise<SummaryResponse['data']> => {
          const response = await api.get<SummaryResponse>(
            `/group-configuration/${group_config_id}/leads/summary/`,
          );

          return response?.data as SummaryResponse['data'];
        },
        {
          enabled: !!(group_config_id && group_config_id !== 0),
          staleTime: 1000 * 60 * 60 * 24,
        },
      );
    },
    [api],
  );

  const invalidateGetCollectionSummary = useCallback(() => {
    batchInvalidateQueries(['collection-summary']);
  }, [batchInvalidateQueries]);

  const getCollectionSummary = useCallback(
    (collection_id: number | null) => {
      return useQuery(
        ['collection-summary', collection_id],
        async (): Promise<SummaryResponse['data']> => {
          const response = await api.get<SummaryResponse>(
            `/collections/${collection_id}/summary/`,
          );

          return response?.data as SummaryResponse['data'];
        },
        {
          enabled: !!(collection_id && collection_id !== 0),
        },
      );
    },
    [api],
  );

  return {
    getGroupConfigLeadsSummary,
    invalidateGetGroupConfigLeadSummary,
    getCollectionSummary,
    invalidateGetCollectionSummary,
  };
};
